import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, FormControl, IconButton, InputLabel,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoiceLineCreateDTO } from '../../interfaces/sales/salesInvoice.interface';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { SalesSettings } from '../../interfaces/sales/salesSettings.interface';
import { AccountService } from '../../services/account.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SegmentService } from '../../services/segment.service';
import { SegmentSettingsService } from '../../services/segment-settings.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

export const isInvoiceSegmentRequired = (revenueAccountId: string | undefined, accounts: Account[]): boolean =>
    !!revenueAccountId && accounts.find(a => a.id === revenueAccountId)?.segmentFieldPolicy === 'REQUIRED';

const today = () => new Date().toISOString().slice(0, 10);

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// In-state row model: the DTO fields (quantity/unitPrice/taxRatePct may be NaN
// while the user is typing) plus a stable client-side key that is never sent
// to the server.
interface LineRow extends Omit<SalesInvoiceLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: string;
  quantity: number;   // may be NaN while field is empty
  unitPrice: number;  // may be NaN while field is empty
  taxRatePct: number; // may be NaN while field is empty
}

// Helpers that treat NaN as 0 for live-total computation only.
const safeLineNet = (row: LineRow): number => {
  const q = Number.isFinite(row.quantity) ? row.quantity : 0;
  const p = Number.isFinite(row.unitPrice) ? row.unitPrice : 0;
  return +((q * p)).toFixed(4);
};
const safeLineTax = (row: LineRow): number => {
  const t = Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0;
  return +((safeLineNet(row) * t) / 100).toFixed(4);
};

const SalesInvoiceCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Stable key counter — bumped each time a new row is created.
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  // Header state
  const [customerId, setCustomerId] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(today());
  const [notes, setNotes] = useState('');

  // Reference data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [settings, setSettings] = useState<SalesSettings | null>(null);
  const [defaultSegmentId, setDefaultSegmentId] = useState<string>('');

  // Lines — stored as LineRow[] (includes transient key)
  const [lines, setLines] = useState<LineRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Track whether user has manually changed due date
  const [dueDateTouched, setDueDateTouched] = useState(false);

  const makeBlankLine = (s: SalesSettings | null): LineRow => ({
    key: nextKey(),
    description: '',
    quantity: 1,
    unitPrice: NaN,
    taxRatePct: s?.defaultTaxRatePct ?? 0,
    revenueAccountId: undefined,
    segmentId: undefined,
  });

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!;
    Promise.all([
      CustomerService.listByInstitution(institutionId, 0, 200),
      SalesSettingsService.getByInstitution(institutionId).catch(() => null),
      AccountService.getAllAccounts(),
      SegmentService.getByInstitution(0, 200),
      SegmentSettingsService.get().catch(() => null),
    ])
      .then(([customerPage, salesSettings, allAccounts, segmentPage, segSettings]) => {
        setCustomers(customerPage.content);
        setSettings(salesSettings);

        setRevenueAccounts(
          Array.isArray(allAccounts)
            ? allAccounts.filter(a => a.accountType === 'REVENUE')
            : []
        );

        const allSegments: Segment[] = segmentPage.content ?? [];
        setSegments(allSegments.filter(s => s.active !== false));

        setDefaultSegmentId(segSettings?.defaultSegmentId ?? '');

        // Set default due date from settings
        if (salesSettings?.defaultPaymentTermsDays) {
          setDueDate(addDays(today(), salesSettings.defaultPaymentTermsDays));
        }

        // Start with one blank line
        setLines([makeBlankLine(salesSettings)]);
      })
      .catch(e => toast.showApiError(e, 'Failed to load invoice data'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIssueDateChange = (newDate: string) => {
    setIssueDate(newDate);
    if (!dueDateTouched && settings?.defaultPaymentTermsDays) {
      setDueDate(addDays(newDate, settings.defaultPaymentTermsDays));
    }
  };

  const handleDueDateChange = (newDate: string) => {
    setDueDate(newDate);
    setDueDateTouched(true);
  };

  const handleAddLine = () => {
    setLines(prev => [...prev, makeBlankLine(settings)]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = <K extends keyof LineRow>(
    index: number,
    field: K,
    value: LineRow[K]
  ) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Live totals — NaN fields treated as 0.
  const subtotal = lines.reduce((s, l) => s + safeLineNet(l), 0);
  const taxAmount = lines.reduce((s, l) => s + safeLineTax(l), 0);
  const total = subtotal + taxAmount;

  const isSubmitDisabled =
    saving ||
    !customerId ||
    lines.length === 0 ||
    lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0));

  const handleSubmit = async () => {
    if (!customerId) { toast.showApiError(null, 'Select a customer'); return; }
    if (lines.length === 0) { toast.showApiError(null, 'Add at least one line'); return; }
    if (lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0))) {
      toast.showApiError(null, 'All lines must have a positive quantity and unit price');
      return;
    }
    if (lines.some(l => isInvoiceSegmentRequired(l.revenueAccountId, revenueAccounts) && !l.segmentId)) {
      toast.showApiError(null, 'Select a segment for every line whose revenue account requires one');
      return;
    }

    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution()!;
      const dto = {
        customerId,
        issueDate,
        dueDate,
        notes: notes || undefined,
        institutionId,
        // Strip the transient key; coerce taxRatePct NaN→0 (qty/price are
        // finite because the submit guard above already checked > 0).
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
        })),
      };
      const created = await SalesInvoiceService.create(dto);
      toast.showSuccess('Sales Invoice created');
      navigate(`/sales/invoices/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create Sales Invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>New Sales Invoice</Typography>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Customer"
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
            required
          >
            {customers.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Issue Date"
            type="date"
            value={issueDate}
            onChange={e => handleIssueDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => handleDueDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />
        </Box>
      </Paper>

      {/* Lines */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Invoice Lines</Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddLine}>
            Add Line
          </Button>
        </Box>

        {lines.length === 0 ? (
          <Typography color="text.secondary">No lines added yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Tax %</TableCell>
                <TableCell>Revenue Account</TableCell>
                <TableCell>Segment</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.key}>
                  <TableCell>
                    <TextField
                      size="small"
                      value={line.description}
                      onChange={e => handleLineChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      sx={{ minWidth: 160 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.quantity) ? line.quantity : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'quantity', v === '' ? NaN : Number(v));
                      }}
                      error={!(line.quantity > 0)}
                      inputProps={{ step: 1, min: 0 }}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'unitPrice', v === '' ? NaN : Number(v));
                      }}
                      error={!(line.unitPrice > 0)}
                      inputProps={{ step: 0.0001, min: 0 }}
                      sx={{ width: 110 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                      onChange={e => {
                        const v = e.target.value;
                        handleLineChange(index, 'taxRatePct', v === '' ? NaN : Number(v));
                      }}
                      inputProps={{ step: 0.01, min: 0 }}
                      sx={{ width: 80 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 180 }}>
                      <AccountAutocomplete
                        data-testid={`inv-revenue-${index}`}
                        accounts={revenueAccounts}
                        value={line.revenueAccountId ?? ''}
                        label="Revenue Account"
                        placeholder="—"
                        onChange={accountId => {
                          handleLineChange(index, 'revenueAccountId', accountId || undefined);
                          if (isInvoiceSegmentRequired(accountId || undefined, revenueAccounts) && !line.segmentId && defaultSegmentId) {
                            handleLineChange(index, 'segmentId', defaultSegmentId);
                          }
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160 }}
                                 error={isInvoiceSegmentRequired(line.revenueAccountId, revenueAccounts) && !line.segmentId}>
                      <InputLabel>Segment</InputLabel>
                      <Select
                        data-testid={`inv-segment-${index}`}
                        label="Segment"
                        value={line.segmentId ?? ''}
                        onChange={e => {
                          const val = e.target.value as string;
                          handleLineChange(index, 'segmentId', val || undefined);
                        }}
                      >
                        <MenuItem value="">—</MenuItem>
                        {segments.map(s => (
                          <MenuItem key={s.id} value={s.id!}>{s.code} — {s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">{safeLineNet(line).toFixed(4)}</TableCell>
                  <TableCell align="right">{safeLineTax(line).toFixed(4)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveLine(index)}
                      aria-label="remove line"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Totals */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body1">{subtotal.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body1">{taxAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6">{total.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {saving ? <CircularProgress size={20} /> : 'Create Invoice'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SalesInvoiceCreate;
